import { isBefore, differenceInDays } from 'date-fns';
import { z } from 'zod';

import { MAX_MFA_NUDGE_DISMISSALS, MFA_NUDGE_STATUS_KEY } from '../constants';

import { getLocalStorageItem } from '@/modules/storage';
import { safeJsonParse } from '@/modules/storage/helpers';

// Zod schema to validate MFA nudge state
const mfaNudgeStateSchema = z.object({
  dismissals: z
    .array(z.string().datetime())
    .min(1)
    .max(MAX_MFA_NUDGE_DISMISSALS),
  snoozedUntil: z.string().datetime().optional(),
});

export interface MFANudgeState {
  dismissals: string[]; // ISO strings, in chronological order
  snoozedUntil?: string; // ISO string, set only after 3rd dismissal
}

export function getMfaNudgeStatus(): boolean {
  const storedStatus = getLocalStorageItem(MFA_NUDGE_STATUS_KEY);
  const { dismissals, snoozedUntil } =
    safeJsonParse<MFANudgeState>(storedStatus, null) || {};

  const validationResult = mfaNudgeStateSchema.safeParse({
    dismissals,
    snoozedUntil,
  });

  const now = new Date();

  // Status does not exist or parsing fails we show the nudge
  if (!storedStatus || !dismissals || !validationResult.success) {
    return true;
  }

  const dismissalCount = dismissals.length;

  // If count is 3, check the date for snoozedUntil
  if (dismissalCount >= MAX_MFA_NUDGE_DISMISSALS && snoozedUntil) {
    const snoozedUntilDate = new Date(snoozedUntil);

    // If date is in the past, clear the dismissals array, snoozedUntil, set first value in dismissals array to Date.now() and return true
    if (isBefore(snoozedUntilDate, new Date())) {
      return true;
    }

    // If date is in the future, return false
    return false;
  }

  if (dismissalCount < MAX_MFA_NUDGE_DISMISSALS) {
    const lastDismissalDate = new Date(dismissals[0]);

    // Check if the first value in the dismissals array is at least 1 day old
    if (differenceInDays(now, lastDismissalDate) >= 1) {
      return true;
    }

    return false;
  }

  return true;
}
